import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getLeaveRequests, approveLeaveRequest, rejectLeaveRequest, cancelLeaveRequest,
  editLeaveRequest,
  getLeaveTypes, createLeaveType, updateLeaveType, deleteLeaveType,
  getHolidays, addHoliday, deleteHoliday,
  getLeaveTeamSummary, yearEndRollover,
  getMyLeaveBalance, submitLeaveRequest,
  getTeamLeaveRequests,
  overrideLeaveBalance, revertLeaveBalance,
  earnCompOff, getCompOffEarnings,
  getEmployees,
  getErrorMessage,
} from "../api";
import { requiredTrimmed, requiredValue, collectErrors } from "../utils/validate";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { SkeletonRow } from "../components/Skeleton";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { useUrlSyncedTab } from "../hooks/useUrlSyncedTab";
import ReportYearPicker from "../components/ReportYearPicker";

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  pending:  { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  approved: { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  rejected: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  cancelled:{ bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1" },
};

type Tab = "overview" | "requests" | "myteam" | "calendar" | "types" | "holidays" | "myleave" | "compoff";
const VALID_TABS: readonly Tab[] = ["overview", "requests", "myteam", "calendar", "types", "holidays", "myleave", "compoff"];

export default function LeaveManagementPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useUrlSyncedTab<Tab>(VALID_TABS, "overview");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // "Log Leave for Employee" modal state
  const [showOnBehalfModal, setShowOnBehalfModal] = useState(false);

  // Leave requests
  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["leave-requests", statusFilter],
    queryFn: () => getLeaveRequests({ status: statusFilter || undefined }),
  });

  const approveMut = useMutation({
    mutationFn: approveLeaveRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      qc.invalidateQueries({ queryKey: ["leave-team"] });
      toast("Leave request approved");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to approve", "error"),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => rejectLeaveRequest(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      setRejectId(null);
      setRejectReason("");
      toast("Leave request rejected", "info");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to reject", "error"),
  });

  const cancelMut = useMutation({
    mutationFn: cancelLeaveRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      toast("Leave request cancelled", "info");
    },
  });

  // Leave types
  const { data: types = [] } = useQuery({ queryKey: ["leave-types"], queryFn: getLeaveTypes });
  const [newType, setNewType] = useState({
    name: "", days_per_year: 10, carry_forward: false, is_paid: true,
    prorate_on_joining: true, allow_lwp: false, is_comp_off: false, color: "#1e3a8a",
  });
  const [showTypeForm, setShowTypeForm] = useState(false);

  const createTypeMut = useMutation({
    mutationFn: createLeaveType,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-types"] });
      setShowTypeForm(false);
      setNewType({ name: "", days_per_year: 10, carry_forward: false, is_paid: true, prorate_on_joining: true, allow_lwp: false, is_comp_off: false, color: "#1e3a8a" });
      toast("Leave type created");
    },
    onError: (e: any) => toast(getErrorMessage(e, "Failed to create leave type"), "error"),
  });

  const deleteTypeMut = useMutation({
    mutationFn: deleteLeaveType,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-types"] });
      toast("Leave type removed", "info");
    },
  });

  const updateTypeMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => updateLeaveType(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave-types"] }); setEditingTypeId(null); },
    onError: () => toast("Failed to update leave type", "error"),
  });

  const [editingTypeId, setEditingTypeId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  // Holidays
  const currentYear = new Date().getFullYear();
  const { data: holidays = [] } = useQuery({
    queryKey: ["holidays", currentYear],
    queryFn: () => getHolidays(currentYear),
  });
  const [newHoliday, setNewHoliday] = useState({ name: "", date: "", location: "" });
  const [typeNameError, setTypeNameError] = useState<string | null>(null);
  const [editNameError, setEditNameError] = useState<string | null>(null);
  const [holidayErrors, setHolidayErrors] = useState<Record<string, string>>({});
  const [showHolidayForm, setShowHolidayForm] = useState(false);

  const addHolidayMut = useMutation({
    mutationFn: addHoliday,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["holidays"] });
      setShowHolidayForm(false);
      setNewHoliday({ name: "", date: "", location: "" });
      toast("Holiday added");
    },
    onError: (e: any) => toast(getErrorMessage(e, "Failed to add holiday"), "error"),
  });

  const deleteHolidayMut = useMutation({
    mutationFn: deleteHoliday,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays"] }),
  });

  if (user?.role === "employee") {
    return <EmployeeLeaveView />;
  }

  const isHR = ["admin", "admin_assistant", "hr"].includes(user?.role ?? "");

  return (
    <div>
      <LoadingOverlay active={
        approveMut.isPending || rejectMut.isPending || cancelMut.isPending || createTypeMut.isPending ||
        updateTypeMut.isPending || addHolidayMut.isPending || deleteTypeMut.isPending || deleteHolidayMut.isPending
      } />
      <div className="page-header">
        <div>
          <h1>Leave Management</h1>
          <p>Manage team leave requests, balances, and public holidays</p>
        </div>
      </div>

      <div className="tab-pill-bar" style={{ marginBottom: 24 }}>
        {(["overview", "requests", "myteam", "calendar", "types", "holidays", "compoff", "myleave"] as Tab[])
          .filter(t => (t !== "myleave" || !isHR) && (t !== "myteam" || !isHR))
          .map(t => {
          const labels: Record<Tab, string> = {
            overview: "Team Overview",
            requests: "Leave Requests",
            myteam: "My Team",
            calendar: "Calendar",
            types: "Leave Types",
            holidays: "Public Holidays",
            compoff: "Comp-off",
            myleave: "My Leave",
          };
          return (
            <button key={t} onClick={() => setTab(t)} className={tab === t ? "active" : ""}>
              {labels[t]}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <TeamOverviewTab />}
      {tab === "myteam" && <MyTeamTab approveMut={approveMut} rejectMut={rejectMut} cancelMut={cancelMut} rejectId={rejectId} setRejectId={setRejectId} rejectReason={rejectReason} setRejectReason={setRejectReason} />}
      {tab === "myleave" && <EmployeeLeaveView />}
      {tab === "calendar" && <LeaveCalendarTab />}
      {tab === "compoff" && <CompOffTab types={types as any[]} />}

      {/* ── Leave Requests ── */}
      {tab === "requests" && (
        <div>
          <div className="leave-req-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div className="filter-bar" style={{ display: "flex", gap: 8 }}>
              {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["cancelled", "Cancelled"], ["", "All"]].map(([s, label]) => (
                <button key={s || "all"} onClick={() => setStatusFilter(s)} style={{
                  padding: "6px 14px", borderRadius: 20, border: "1px solid #e2e8f0",
                  background: statusFilter === s ? "#1e3a8a" : "white",
                  color: statusFilter === s ? "white" : "#64748b",
                  cursor: "pointer", fontSize: 13, fontWeight: 500,
                }}>
                  {label}
                </button>
              ))}
            </div>
            {isHR && (
              <button onClick={() => setShowOnBehalfModal(true)} style={primaryBtnStyle}>
                + Log Leave for Employee
              </button>
            )}
          </div>

          {isLoading && <div style={{ display: "grid", gap: 10 }}><SkeletonRow cols={3} /><SkeletonRow cols={3} /><SkeletonRow cols={3} /></div>}

          {/* Reject modal */}
          {rejectId !== null && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
              <div className="modal-sheet" style={{ background: "white", borderRadius: 12, padding: 24 }}>
                <h3 style={{ margin: "0 0 14px", color: "#1e3a8a" }}>Reject Leave Request</h3>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  placeholder="Reason for rejection (optional)"
                  style={{ width: "100%", height: 80, padding: 8, border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
                  <button onClick={() => setRejectId(null)} style={outlineBtnStyle}>Cancel</button>
                  <button onClick={() => rejectMut.mutate({ id: rejectId!, reason: rejectReason })}
                    disabled={rejectMut.isPending}
                    style={{ ...outlineBtnStyle, background: "#dc2626", color: "white", borderColor: "#dc2626" }}>
                    Reject
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            {(requests as any[]).map((req: any) => {
              const sc = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
              return (
                <div key={req.id} className="list-row leave-request-item" style={{
                  background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
                  padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 15, color: "#1e293b" }}>{req.user_name}</span>
                      <span style={{ fontSize: 13, color: "#64748b" }}>· {req.leave_type}</span>
                      <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{req.status}</span>
                      {req.is_lwp && <span style={{ background: "#fce7f3", color: "#9d174d", border: "1px solid #fbcfe8", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>LWP</span>}
                      {req.logged_by && <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", padding: "2px 8px", borderRadius: 20, fontSize: 11 }}>On Behalf</span>}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {formatDate(req.start_date)} → {formatDate(req.end_date)}
                      <span style={{ marginLeft: 10, fontWeight: 600, color: "#1e293b" }}>{req.days} day{req.days !== 1 ? "s" : ""}{req.half_day ? " (half)" : ""}</span>
                    </div>
                    {req.reason && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>{req.reason}</div>}
                    {req.rejection_reason && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 3 }}>Rejection: {req.rejection_reason}</div>}
                  </div>
                  <div className="request-actions" style={{ display: "flex", gap: 8 }}>
                    {req.status === "pending" && (<>
                      <button onClick={() => approveMut.mutate(req.id)} disabled={approveMut.isPending} style={{ background: "#dcfce7", color: "#166534", border: "1px solid #86efac", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Approve</button>
                      <button onClick={() => setRejectId(req.id)} style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Reject</button>
                    </>)}
                    {(req.status === "pending" || req.status === "approved") && (
                      <AsyncButton onClick={async () => { if (await confirm(`Cancel ${req.user_name}'s leave request?`, { title: "Cancel Leave", confirmLabel: "Cancel Leave", danger: false })) cancelMut.mutate(req.id); }} style={{ background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Cancel</AsyncButton>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!isLoading && (requests as any[]).length === 0 && (
            <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8" }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 10 }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="8" y1="18" x2="14" y2="18"/></svg>
              <div>No {statusFilter} leave requests.</div>
            </div>
          )}
        </div>
      )}

      {/* ── Leave Types ── */}
      {tab === "types" && isHR && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button onClick={() => setShowTypeForm(!showTypeForm)} style={primaryBtnStyle}>
              {showTypeForm ? "Cancel" : "+ Add Leave Type"}
            </button>
          </div>

          {showTypeForm && (
            <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, marginBottom: 16 }}>
              <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Name *</label>
                  <input value={newType.name} onChange={e => { setNewType(t => ({ ...t, name: e.target.value })); setTypeNameError(null); }} placeholder="e.g. Annual Leave" style={inputStyle} />
                  {typeNameError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{typeNameError}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Days per Year</label>
                  <input type="number" min={0} max={365} value={newType.days_per_year} onChange={e => setNewType(t => ({ ...t, days_per_year: parseFloat(e.target.value) }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Color</label>
                  <input type="color" value={newType.color} onChange={e => setNewType(t => ({ ...t, color: e.target.value }))} style={{ ...inputStyle, height: 36, padding: 2, cursor: "pointer" }} />
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-end", paddingBottom: 4, flexWrap: "wrap" }}>
                  {[
                    { key: "carry_forward", label: "Carry forward" },
                    { key: "is_paid", label: "Paid leave" },
                    { key: "prorate_on_joining", label: "Pro-rate on joining" },
                    { key: "allow_lwp", label: "Allow LWP" },
                    { key: "is_comp_off", label: "Comp-off type" },
                  ].map(({ key, label }) => (
                    <label key={key} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
                      <input type="checkbox" checked={(newType as any)[key]} onChange={e => setNewType(t => ({ ...t, [key]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <button onClick={() => {
                const err = requiredTrimmed(newType.name, "Name");
                setTypeNameError(err);
                if (!err) createTypeMut.mutate({ ...newType, name: newType.name.trim() });
              }} disabled={createTypeMut.isPending} style={primaryBtnStyle}>
                Create Leave Type
              </button>
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            {(types as any[]).map((t: any) => {
              const isEditing = editingTypeId === t.id;
              return (
                <div key={t.id} style={{ background: "white", border: `1px solid ${isEditing ? "#93c5fd" : "#e2e8f0"}`, borderRadius: 10, borderLeft: `4px solid ${t.color}`, overflow: "hidden" }}>
                  <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
                        {t.name}
                        {t.allow_lwp && <span style={{ background: "#fce7f3", color: "#9d174d", border: "1px solid #fbcfe8", padding: "1px 7px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>LWP</span>}
                        {t.is_comp_off && <span style={{ background: "#f0fdf4", color: "#166534", border: "1px solid #86efac", padding: "1px 7px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Comp-off</span>}
                      </div>
                      <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                        {t.days_per_year} days/year · {t.is_paid ? "Paid" : "Unpaid"}
                        {t.carry_forward ? ` · Carry forward${t.max_carry_days ? ` up to ${t.max_carry_days}d` : ""}` : ""}
                        · <span style={{ color: t.prorate_on_joining ? "#1d4ed8" : "#94a3b8" }}>{t.prorate_on_joining ? "Pro-rated" : "Not pro-rated"}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { if (isEditing) { setEditingTypeId(null); } else { setEditingTypeId(t.id); setEditForm({ ...t }); } }}
                        style={{ padding: "5px 14px", borderRadius: 6, border: `1px solid ${isEditing ? "#93c5fd" : "#e2e8f0"}`, background: isEditing ? "#eff6ff" : "white", fontSize: 12, fontWeight: 600, cursor: "pointer", color: isEditing ? "#1d4ed8" : "#475569" }}>
                        {isEditing ? "Cancel" : "Edit"}
                      </button>
                      <button onClick={() => deleteTypeMut.mutate(t.id)} disabled={deleteTypeMut.isPending} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid #fca5a5", background: "white", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#dc2626" }}>Remove</button>
                    </div>
                  </div>
                  {isEditing && (
                    <div style={{ padding: "0 18px 18px", borderTop: "1px solid #f1f5f9" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px", gap: 12, marginTop: 14, marginBottom: 12 }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Name</label>
                          <input value={editForm.name} onChange={e => { setEditForm((f: any) => ({ ...f, name: e.target.value })); setEditNameError(null); }} style={inputStyle} />
                          {editNameError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{editNameError}</div>}
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Days per Year</label>
                          <input type="number" min={0} max={365} value={editForm.days_per_year} onChange={e => setEditForm((f: any) => ({ ...f, days_per_year: parseFloat(e.target.value) }))} style={inputStyle} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Color</label>
                          <input type="color" value={editForm.color} onChange={e => setEditForm((f: any) => ({ ...f, color: e.target.value }))} style={{ ...inputStyle, height: 36, padding: 2, cursor: "pointer" }} />
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
                        {[
                          { key: "carry_forward", label: "Carry forward" },
                          { key: "is_paid", label: "Paid leave" },
                          { key: "prorate_on_joining", label: "Pro-rate on joining" },
                          { key: "allow_lwp", label: "Allow LWP" },
                          { key: "is_comp_off", label: "Comp-off type" },
                        ].map(({ key, label }) => (
                          <label key={key} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
                            <input type="checkbox" checked={!!editForm[key]} onChange={e => setEditForm((f: any) => ({ ...f, [key]: e.target.checked }))} />
                            {label}
                          </label>
                        ))}
                        {editForm.carry_forward && (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <label style={{ fontSize: 13, color: "#64748b" }}>Max carry days</label>
                            <input type="number" value={editForm.max_carry_days || 0} onChange={e => setEditForm((f: any) => ({ ...f, max_carry_days: parseFloat(e.target.value) }))} style={{ ...inputStyle, width: 70 }} />
                          </div>
                        )}
                      </div>
                      <button onClick={() => {
                        const err = requiredTrimmed(editForm.name, "Name");
                        setEditNameError(err);
                        if (!err) updateTypeMut.mutate({ id: t.id, data: { ...editForm, name: editForm.name.trim() } });
                      }} disabled={updateTypeMut.isPending} style={primaryBtnStyle}>
                        {updateTypeMut.isPending ? "Saving…" : "Save Changes"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <YearEndRolloverSection />
        </div>
      )}
      {tab === "types" && !isHR && (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Leave type management requires admin access.</div>
      )}

      {/* ── Public Holidays ── */}
      {tab === "holidays" && isHR && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button onClick={() => setShowHolidayForm(!showHolidayForm)} style={primaryBtnStyle}>
              {showHolidayForm ? "Cancel" : `+ Add ${currentYear} Holiday`}
            </button>
          </div>
          {showHolidayForm && (
            <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, marginBottom: 16 }}>
              <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Holiday Name *</label>
                  <input value={newHoliday.name} onChange={e => { setNewHoliday(h => ({ ...h, name: e.target.value })); setHolidayErrors(er => ({ ...er, name: "" })); }} placeholder="e.g. New Year's Day" style={inputStyle} />
                  {holidayErrors.name && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{holidayErrors.name}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Date *</label>
                  <DateInput value={newHoliday.date} onChange={e => { setNewHoliday(h => ({ ...h, date: e.target.value })); setHolidayErrors(er => ({ ...er, date: "" })); }} style={inputStyle} />
                  {holidayErrors.date && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{holidayErrors.date}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Office Location <span style={{ fontWeight: 400, color: "#94a3b8" }}>(optional)</span></label>
                  <input value={newHoliday.location} onChange={e => setNewHoliday(h => ({ ...h, location: e.target.value }))} placeholder="e.g. Bangkok, Singapore" style={inputStyle} />
                </div>
              </div>
              <button onClick={() => {
                const errors = collectErrors({
                  name: requiredTrimmed(newHoliday.name, "Holiday name"),
                  date: requiredValue(newHoliday.date, "Date"),
                });
                setHolidayErrors(errors);
                if (!Object.keys(errors).length) addHolidayMut.mutate({ ...newHoliday, name: newHoliday.name.trim() });
              }} disabled={addHolidayMut.isPending} style={primaryBtnStyle}>Add Holiday</button>
            </div>
          )}
          <div style={{ display: "grid", gap: 8 }}>
            {(holidays as any[]).map((h: any) => (
              <div key={h.id} className="list-row" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#1e293b" }}>{h.name}</span>
                  <span style={{ marginLeft: 12, color: "#64748b", fontSize: 13 }}>{h.date}</span>
                  {h.location
                    ? <span style={{ marginLeft: 10, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 12, padding: "2px 8px", fontSize: 11 }}>{h.location}</span>
                    : <span style={{ marginLeft: 10, color: "#94a3b8", fontSize: 11 }}>All offices</span>}
                </div>
                <button onClick={() => deleteHolidayMut.mutate(h.id)} disabled={deleteHolidayMut.isPending} style={outlineBtnStyle}>Remove</button>
              </div>
            ))}
          </div>
          {(holidays as any[]).length === 0 && (
            <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8" }}>No public holidays configured for {currentYear}.</div>
          )}
        </div>
      )}
      {tab === "holidays" && !isHR && (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Holiday management requires admin access.</div>
      )}

      {/* "Log Leave for Employee" modal */}
      {showOnBehalfModal && (
        <OnBehalfModal
          types={types as any[]}
          onClose={() => setShowOnBehalfModal(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ["leave-requests"] });
            qc.invalidateQueries({ queryKey: ["leave-team"] });
            setShowOnBehalfModal(false);
            toast("Leave logged for employee");
          }}
        />
      )}
    </div>
  );
}


// ── On Behalf Modal ─────────────────────────────────────────────────────────

function OnBehalfModal({ types, onClose, onSuccess }: {
  types: any[]; onClose: () => void; onSuccess: () => void;
}) {
  const { toast } = useToast();

  // Fetch ALL employees in the tenant — not filtered by manager relationship
  const { data: employees = [] } = useQuery({
    queryKey: ["all-employees"],
    queryFn: getEmployees,
  });

  const [form, setForm] = useState({
    on_behalf_of_user_id: "",
    leave_type_id: "",
    start_date: "",
    end_date: "",
    half_day: false,
    reason: "",
  });

  const submitMut = useMutation({
    mutationFn: submitLeaveRequest,
    onSuccess,
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to submit leave", "error"),
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
      <LoadingOverlay active={submitMut.isPending} />
      <div className="modal-sheet" style={{ background: "white", borderRadius: 14, padding: 28, width: 480, maxWidth: "95vw" }}>
        <h3 style={{ margin: "0 0 20px", color: "#1e3a8a", fontSize: 17 }}>Log Leave for Employee</h3>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Employee *</label>
          <select value={form.on_behalf_of_user_id} onChange={e => setForm(f => ({ ...f, on_behalf_of_user_id: e.target.value }))} style={inputStyle}>
            <option value="">Select employee</option>
            {employees.map((e: any) => <option key={e.id} value={e.id}>{e.name}{e.employee_id ? ` (${e.employee_id})` : ""}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Leave Type *</label>
          <select value={form.leave_type_id} onChange={e => setForm(f => ({ ...f, leave_type_id: e.target.value }))} style={inputStyle}>
            <option value="">Select leave type</option>
            {types.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>From *</label>
            <DateInput value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>To *</label>
            <DateInput value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={inputStyle} />
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", color: "#475569" }}>
            <input type="checkbox" checked={form.half_day} onChange={e => setForm(f => ({ ...f, half_day: e.target.checked }))} />
            Half day
          </label>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Reason</label>
          <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={outlineBtnStyle}>Cancel</button>
          <button
            onClick={() => {
              if (!form.on_behalf_of_user_id || !form.leave_type_id || !form.start_date || !form.end_date) {
                toast("Please fill in all required fields", "error"); return;
              }
              if (form.end_date < form.start_date) {
                toast("End date cannot be before start date", "error"); return;
              }
              submitMut.mutate({ ...form, on_behalf_of_user_id: parseInt(form.on_behalf_of_user_id), leave_type_id: parseInt(form.leave_type_id) });
            }}
            disabled={submitMut.isPending}
            style={{ ...primaryBtnStyle, opacity: submitMut.isPending ? 0.7 : 1 }}>
            {submitMut.isPending ? "Submitting…" : "Submit Leave"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Employee self-service view ────────────────────────────────────────────────

function EmployeeLeaveView() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();
  const currentYear = new Date().getFullYear();
  const [tab, setTab] = useState<"balance" | "requests" | "apply" | "holidays">("balance");
  const [showApplyForm, setShowApplyForm] = useState(false);

  // My Reports (self-service leave utilization) was retired into this tab. It
  // showed strictly less than these cards already do, apart from a year
  // selector and an explicit utilization figure — both carried over below so
  // nothing is lost in the move (spec reports-v2-shell-and-people-analytics,
  // Package 1 AC3).
  const [balanceYear, setBalanceYear] = useState(currentYear);
  const { data: balance = [], isLoading: balLoading } = useQuery({
    queryKey: ["my-leave-balance", balanceYear],
    queryFn: () => getMyLeaveBalance(balanceYear),
  });

  const { data: requests = [], isLoading: reqLoading } = useQuery({
    queryKey: ["leave-requests"],
    queryFn: () => getLeaveRequests({}),
  });

  const { data: types = [] } = useQuery({ queryKey: ["leave-types"], queryFn: getLeaveTypes });
  const { data: holidays = [] } = useQuery({ queryKey: ["holidays", currentYear], queryFn: () => getHolidays(currentYear) });

  const [form, setForm] = useState({ leave_type_id: "", start_date: "", end_date: "", reason: "", half_day: false });

  // Feature 4: edit state
  const [editingReqId, setEditingReqId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  const submitMut = useMutation({
    mutationFn: submitLeaveRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      qc.invalidateQueries({ queryKey: ["my-leave-balance"] });
      setForm({ leave_type_id: "", start_date: "", end_date: "", reason: "", half_day: false });
      setShowApplyForm(false);
      setTab("requests");
      toast("Leave request submitted");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to submit request", "error"),
  });

  const editMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => editLeaveRequest(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      setEditingReqId(null);
      toast("Leave request updated");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to update request", "error"),
  });

  const cancelMut = useMutation({
    mutationFn: cancelLeaveRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      qc.invalidateQueries({ queryKey: ["my-leave-balance"] });
      toast("Leave request cancelled", "info");
    },
  });

  const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    pending:  { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
    approved: { bg: "#dcfce7", text: "#166534", border: "#86efac" },
    rejected: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
    cancelled:{ bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1" },
  };

  return (
    <div>
      <LoadingOverlay active={submitMut.isPending || editMut.isPending || cancelMut.isPending} />
      <div className="page-header">
        <div>
          <h1>My Leaves</h1>
          <p>View your leave balance, apply for leave, and track requests</p>
        </div>
        <button onClick={() => { setShowApplyForm(true); setTab("apply"); }} style={primaryBtnStyle}>+ Apply Leave</button>
      </div>

      <div className="tab-pill-bar" style={{ marginBottom: 24 }}>
        {(["balance", "requests", "apply", "holidays"] as const).map(t => {
          const labels = { balance: "My Balance", requests: "My Requests", apply: "Apply Leave", holidays: "Holidays" };
          return <button key={t} onClick={() => setTab(t)} className={tab === t ? "active" : ""}>{labels[t]}</button>;
        })}
      </div>

      {/* Balance tab */}
      {tab === "balance" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, color: "#1e293b" }}>Leave Balance — {balanceYear}</h3>
            <ReportYearPicker year={balanceYear} onChange={setBalanceYear} thisYear={currentYear} />
          </div>
          {balLoading && <><SkeletonRow cols={3} /><SkeletonRow cols={3} /></>}
          {!balLoading && (balance as any[]).length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
              {balanceYear === currentYear
                ? "No leave types configured yet."
                : `No leave balances recorded for ${balanceYear}.`}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
            {(balance as any[]).map((b: any) => {
              const pct = b.allocated > 0 ? Math.min(100, (b.used / (b.allocated + b.carried_forward)) * 100) : 0;
              return (
                <div key={b.leave_type_id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: b.color || "#1e3a8a" }} />
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{b.leave_type_name}</span>
                    {b.is_comp_off && <span style={{ fontSize: 10, color: "#166534", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: "1px 6px" }}>Comp-off</span>}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#1e3a8a", lineHeight: 1 }}>{b.available}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>days available</div>
                  {b.allow_lwp && <div style={{ fontSize: 11, color: "#9d174d", marginTop: 3 }}>(LWP allowed)</div>}
                  <div style={{ background: "#f1f5f9", borderRadius: 4, height: 6, marginTop: 10, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: b.color || "#1e3a8a", borderRadius: 4 }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94a3b8", marginTop: 5 }}>
                    <span>Used: {b.used}</span>
                    <span>{Math.round(pct)}% used</span>
                    <span>Total: {b.allocated + (b.carried_forward || 0)}</span>
                  </div>
                  {b.carried_forward > 0 && <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>+{b.carried_forward} carried forward</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Apply tab */}
      {tab === "apply" && (
        <div style={{ maxWidth: 520 }}>
          <h3 style={{ margin: "0 0 20px", fontSize: 15, color: "#1e293b" }}>Apply for Leave</h3>
          <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24 }}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Leave Type *</label>
              <select value={form.leave_type_id} onChange={e => setForm(f => ({ ...f, leave_type_id: e.target.value }))} style={inputStyle}>
                <option value="">Select leave type</option>
                {(types as any[]).map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.allow_lwp ? " (LWP allowed)" : ""}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>From *</label>
                <DateInput value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>To *</label>
                <DateInput value={form.end_date} min={form.start_date || undefined} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", color: "#475569" }}>
                <input type="checkbox" checked={form.half_day} onChange={e => setForm(f => ({ ...f, half_day: e.target.checked }))} />
                Half day
              </label>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Reason</label>
              <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Optional" rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => {
                  if (!form.leave_type_id || !form.start_date || !form.end_date) { toast("Please fill in all required fields", "error"); return; }
                  if (form.end_date < form.start_date) { toast("End date cannot be before start date", "error"); return; }
                  submitMut.mutate({ ...form, leave_type_id: parseInt(form.leave_type_id) });
                }}
                disabled={submitMut.isPending}
                style={{ ...primaryBtnStyle, opacity: submitMut.isPending ? 0.7 : 1 }}>
                {submitMut.isPending ? "Submitting..." : "Submit Request"}
              </button>
              <button onClick={() => { setForm({ leave_type_id: "", start_date: "", end_date: "", reason: "", half_day: false }); setTab("balance"); }} style={outlineBtnStyle}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Requests tab */}
      {tab === "requests" && (
        <div>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, color: "#1e293b" }}>My Leave Requests</h3>
          {reqLoading && <><SkeletonRow cols={3} /><SkeletonRow cols={3} /></>}
          {!reqLoading && (requests as any[]).length === 0 && (
            <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8" }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 10 }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="8" y1="18" x2="14" y2="18"/></svg>
              <div>No leave requests yet.</div>
              <button onClick={() => setTab("apply")} style={{ ...primaryBtnStyle, marginTop: 12, fontSize: 13 }}>Apply for Leave</button>
            </div>
          )}
          <div style={{ display: "grid", gap: 10 }}>
            {(requests as any[]).map((req: any) => {
              const sc = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
              const isEditingThis = editingReqId === req.id;
              return (
                <div key={req.id} style={{ background: "white", border: `1px solid ${isEditingThis ? "#93c5fd" : "#e2e8f0"}`, borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{req.leave_type}</span>
                        <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{req.status}</span>
                        {req.is_lwp && <span style={{ background: "#fce7f3", color: "#9d174d", border: "1px solid #fbcfe8", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>LWP</span>}
                        {req.logged_by && <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", padding: "2px 8px", borderRadius: 20, fontSize: 11 }}>Logged by HR</span>}
                      </div>
                      <div style={{ fontSize: 13, color: "#64748b" }}>
                        {formatDate(req.start_date)} → {formatDate(req.end_date)}
                        <span style={{ marginLeft: 10, fontWeight: 600, color: "#1e293b" }}>{req.days} day{req.days !== 1 ? "s" : ""}{req.half_day ? " (half)" : ""}</span>
                      </div>
                      {req.reason && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>{req.reason}</div>}
                      {req.rejection_reason && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 3 }}>Rejected: {req.rejection_reason}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {req.status === "pending" && (
                        <button
                          onClick={() => {
                            if (isEditingThis) { setEditingReqId(null); }
                            else { setEditingReqId(req.id); setEditForm({ start_date: req.start_date, end_date: req.end_date, half_day: req.half_day, reason: req.reason || "" }); }
                          }}
                          style={{ background: isEditingThis ? "#eff6ff" : "white", border: `1px solid ${isEditingThis ? "#93c5fd" : "#e2e8f0"}`, color: isEditingThis ? "#1d4ed8" : "#475569", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                          {isEditingThis ? "Cancel" : "Edit"}
                        </button>
                      )}
                      {(req.status === "pending" || req.status === "approved") && (
                        <AsyncButton onClick={async () => {
                          if (await confirm("Cancel this leave request?", { confirmLabel: "Cancel Leave", danger: false }))
                            cancelMut.mutate(req.id);
                        }} style={outlineBtnStyle}>Cancel</AsyncButton>
                      )}
                    </div>
                  </div>

                  {/* Inline edit form */}
                  {isEditingThis && (
                    <div style={{ padding: "0 18px 18px", borderTop: "1px solid #f1f5f9" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14, marginBottom: 12 }}>
                        <div>
                          <label style={labelStyle}>From</label>
                          <DateInput value={editForm.start_date} onChange={e => setEditForm((f: any) => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>To</label>
                          <DateInput value={editForm.end_date} min={editForm.start_date || undefined} onChange={e => setEditForm((f: any) => ({ ...f, end_date: e.target.value }))} style={inputStyle} />
                        </div>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", color: "#475569" }}>
                          <input type="checkbox" checked={editForm.half_day} onChange={e => setEditForm((f: any) => ({ ...f, half_day: e.target.checked }))} />
                          Half day
                        </label>
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>Reason</label>
                        <textarea value={editForm.reason} onChange={e => setEditForm((f: any) => ({ ...f, reason: e.target.value }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
                      </div>
                      <button
                        onClick={() => editMut.mutate({ id: req.id, data: editForm })}
                        disabled={editMut.isPending}
                        style={{ ...primaryBtnStyle, fontSize: 13, padding: "8px 16px" }}>
                        {editMut.isPending ? "Saving…" : "Save Changes"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Holidays tab */}
      {tab === "holidays" && (
        <div>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, color: "#1e293b" }}>Public Holidays — {currentYear}</h3>
          {(holidays as any[]).length === 0 && <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>No holidays configured.</div>}
          <div style={{ display: "grid", gap: 8 }}>
            {(holidays as any[]).map((h: any) => (
              <div key={h.id} className="list-row" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{h.name}</div>
                  {h.location && <div style={{ fontSize: 12, color: "#94a3b8" }}>{h.location}</div>}
                </div>
                <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>{h.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Team Overview Tab ────────────────────────────────────────────────────────

function TeamOverviewTab() {
  const tz = useTenantTimezone();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [overrideModal, setOverrideModal] = useState<{ bal: any; emp: any } | null>(null);
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const { data: team = [], isLoading } = useQuery({
    queryKey: ["leave-team", year],
    queryFn: () => getLeaveTeamSummary(year),
  });

  const { data: employeeRequests = [], isFetching: loadingRequests } = useQuery({
    queryKey: ["leave-requests-user", expandedId, year],
    queryFn: () => getLeaveRequests({ user_id: expandedId! }),
    enabled: expandedId !== null,
  });

  const revertMut = useMutation({
    mutationFn: (balanceId: number) => revertLeaveBalance(balanceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-team"] });
      toast("Balance override reverted");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to revert", "error"),
  });

  if (isLoading) return <p style={{ color: "#64748b" }}>Loading...</p>;

  if ((team as any[]).length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
        <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 12 }}><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/></svg>
        <div style={{ fontSize: 15 }}>No employees found. Invite employees via Workspace → Team.</div>
      </div>
    );
  }

  return (
    <div>
      <LoadingOverlay active={revertMut.isPending} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <label style={{ fontSize: 13, color: "#64748b" }}>Year:</label>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 10px", fontSize: 13 }}>
          {[currentYear - 1, currentYear, currentYear + 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Override modal */}
      {overrideModal && (
        <OverrideModal
          bal={overrideModal.bal}
          emp={overrideModal.emp}
          year={year}
          onClose={() => setOverrideModal(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ["leave-team"] });
            setOverrideModal(null);
            toast("Balance override saved");
          }}
        />
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {(team as any[]).map((emp: any) => {
          const isExpanded = expandedId === emp.id;
          const totalUsed = emp.balances.reduce((s: number, b: any) => s + b.used, 0);
          const totalAllocated = emp.balances.reduce((s: number, b: any) => s + b.allocated + b.carried_forward, 0);

          return (
            <div key={emp.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
              <div onClick={() => setExpandedId(isExpanded ? null : emp.id)} style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 16, background: isExpanded ? "#f0f9ff" : "white", transition: "background 0.15s" }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg, #1e3a8a, #00c9a7)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                  {emp.name[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>
                    {emp.name}
                    {emp.pending_requests > 0 && <span style={{ marginLeft: 8, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20 }}>{emp.pending_requests} pending</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>{[emp.employee_id, emp.designation, emp.department].filter(Boolean).join(" · ") || emp.email}</div>
                </div>
                <div className="team-emp-balances" style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {emp.balances.map((b: any) => (
                    <div key={b.leave_type_id} style={{ textAlign: "center", minWidth: 70 }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>{b.leave_type_name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: b.available === 0 ? "#dc2626" : "#1e3a8a" }}>{b.available}</span>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>/ {b.allocated + b.carried_forward}</span>
                        {b.is_override && <span style={{ fontSize: 9, color: "#9d174d", background: "#fce7f3", borderRadius: 8, padding: "0 4px" }}>★</span>}
                      </div>
                      <div style={{ height: 3, background: "#e2e8f0", borderRadius: 2, marginTop: 3, width: 60 }}>
                        <div style={{ height: "100%", borderRadius: 2, background: b.color || "#1e3a8a", width: `${Math.min(100, (b.used / (b.allocated + b.carried_forward || 1)) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="team-emp-total" style={{ textAlign: "right", flexShrink: 0, minWidth: 80 }}>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>Total used</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>{totalUsed} <span style={{ fontSize: 12, fontWeight: 400, color: "#64748b" }}>/ {totalAllocated} days</span></div>
                </div>
                <div style={{ color: "#94a3b8", fontSize: 18, flexShrink: 0 }}>{isExpanded ? "▲" : "▼"}</div>
              </div>

              {isExpanded && (
                <div style={{ borderTop: "1px solid #e2e8f0", padding: "0 18px 14px" }}>
                  {/* Balance detail with override controls */}
                  <div style={{ display: "flex", gap: 12, padding: "12px 0", flexWrap: "wrap" }}>
                    {emp.balances.map((b: any) => (
                      <div key={b.leave_type_id} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: `3px solid ${b.color || "#1e3a8a"}`, borderRadius: 6, padding: "8px 14px", minWidth: 150 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: 12, color: "#334155" }}>{b.leave_type_name}</span>
                          {b.is_override
                            ? (
                              <AsyncButton onClick={async () => {
                                if (await confirm(`Revert override for ${emp.name} – ${b.leave_type_name}?`, { confirmLabel: "Revert" }))
                                  revertMut.mutate(b.id);
                              }} style={{ fontSize: 10, color: "#9d174d", background: "#fce7f3", border: "1px solid #fbcfe8", borderRadius: 8, padding: "1px 6px", cursor: "pointer" }}>
                                ★ Revert
                              </AsyncButton>
                            )
                            : (
                              <button onClick={(e) => { e.stopPropagation(); setOverrideModal({ bal: b, emp }); }} style={{ fontSize: 10, color: "#64748b", background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "1px 6px", cursor: "pointer" }}>
                                Override
                              </button>
                            )
                          }
                        </div>
                        {b.is_override && b.override_note && <div style={{ fontSize: 10, color: "#9d174d", marginBottom: 4 }}>{b.override_note}</div>}
                        <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, textAlign: "center" }}>
                          <div><div style={{ fontSize: 10, color: "#94a3b8" }}>Allocated</div><div style={{ fontWeight: 700, fontSize: 14, color: "#1e3a8a" }}>{b.allocated}</div></div>
                          <div><div style={{ fontSize: 10, color: "#94a3b8" }}>Used</div><div style={{ fontWeight: 700, fontSize: 14, color: "#dc2626" }}>{b.used}</div></div>
                          <div><div style={{ fontSize: 10, color: "#94a3b8" }}>Left</div><div style={{ fontWeight: 700, fontSize: 14, color: b.available === 0 ? "#dc2626" : "#16a34a" }}>{b.available}</div></div>
                        </div>
                        {b.carried_forward > 0 && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>+{b.carried_forward} carried fwd</div>}
                      </div>
                    ))}
                  </div>

                  {/* Request history */}
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 8 }}>Leave History</div>
                  {loadingRequests && <p style={{ fontSize: 12, color: "#94a3b8" }}>Loading...</p>}
                  {!loadingRequests && (employeeRequests as any[]).length === 0 && <p style={{ fontSize: 13, color: "#94a3b8" }}>No leave requests found.</p>}
                  <div style={{ display: "grid", gap: 6 }}>
                    {(employeeRequests as any[]).map((req: any) => {
                      const sc = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
                      return (
                        <div key={req.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: req.leave_type_color || "#1e3a8a", flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{req.leave_type}</span>
                            <span style={{ marginLeft: 10, fontSize: 12, color: "#64748b" }}>{formatDate(req.start_date)} → {formatDate(req.end_date)} · <strong>{req.days} day{req.days !== 1 ? "s" : ""}</strong>{req.half_day ? " (half)" : ""}</span>
                            {req.is_lwp && <span style={{ marginLeft: 6, fontSize: 11, color: "#9d174d", background: "#fce7f3", borderRadius: 8, padding: "0 5px" }}>LWP</span>}
                          </div>
                          <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{req.status}</span>
                          <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>{formatDate(req.created_at, "short", tz)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Override Modal ────────────────────────────────────────────────────────────

function OverrideModal({ bal, emp, year, onClose, onSuccess }: {
  bal: any; emp: any; year: number; onClose: () => void; onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [allocated, setAllocated] = useState(String(bal.allocated));
  const [note, setNote] = useState(bal.override_note || "");

  const overrideMut = useMutation({
    mutationFn: overrideLeaveBalance,
    onSuccess,
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save override", "error"),
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
      <LoadingOverlay active={overrideMut.isPending} />
      <div className="modal-sheet" style={{ background: "white", borderRadius: 14, padding: 28, width: 400, maxWidth: "95vw" }}>
        <h3 style={{ margin: "0 0 6px", color: "#1e3a8a", fontSize: 16 }}>Override Leave Balance</h3>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b" }}>{emp.name} — {bal.leave_type_name} ({year})</p>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Allocated Days *</label>
          <input type="number" step="0.5" value={allocated} onChange={e => setAllocated(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Note (reason for override)</label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Approved by HR Director" style={inputStyle} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={outlineBtnStyle}>Cancel</button>
          <button
            onClick={() => overrideMut.mutate({ user_id: emp.id, leave_type_id: bal.leave_type_id, year, allocated: parseFloat(allocated), note })}
            disabled={overrideMut.isPending || !allocated}
            style={{ ...primaryBtnStyle, opacity: overrideMut.isPending ? 0.7 : 1 }}>
            {overrideMut.isPending ? "Saving…" : "Save Override"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Comp-off Tab ──────────────────────────────────────────────────────────────

function CompOffTab({ types }: { types: any[] }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({ user_id: "", worked_date: "", earned_days: "1", note: "" });

  // Fetch ALL employees — not filtered by manager relationship
  const { data: employees = [] } = useQuery({
    queryKey: ["all-employees"],
    queryFn: getEmployees,
  });

  const compOffTypes = types.filter((t: any) => t.is_comp_off);
  const hasCompOffType = compOffTypes.length > 0;

  const { data: earnings = [], isLoading } = useQuery({
    queryKey: ["comp-off-earnings"],
    queryFn: () => getCompOffEarnings(),
  });

  const earnMut = useMutation({
    mutationFn: earnCompOff,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comp-off-earnings"] });
      qc.invalidateQueries({ queryKey: ["leave-team"] });
      setForm({ user_id: "", worked_date: "", earned_days: "1", note: "" });
      toast("Comp-off day recorded");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to record comp-off", "error"),
  });

  return (
    <div>
      <LoadingOverlay active={earnMut.isPending} />
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 15, color: "#1e293b" }}>Compensatory Leave</h3>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b" }}>Record comp-off days earned by employees who worked on weekends or public holidays.</p>

        {!hasCompOffType && (
          <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#92400e" }}>
            No comp-off leave type configured. Go to <strong>Leave Types</strong> tab and create one with "Comp-off type" enabled.
          </div>
        )}

        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 20, maxWidth: 520 }}>
          <h4 style={{ margin: "0 0 16px", fontSize: 14, color: "#1e293b" }}>Record Comp-off Earning</h4>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Employee *</label>
            <select value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))} style={inputStyle} disabled={!hasCompOffType}>
              <option value="">Select employee</option>
              {employees.map((e: any) => <option key={e.id} value={e.id}>{e.name}{e.employee_id ? ` (${e.employee_id})` : ""}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Worked Date * <span style={{ fontWeight: 400, color: "#94a3b8" }}>(must be weekend/holiday)</span></label>
              <DateInput value={form.worked_date} onChange={e => setForm(f => ({ ...f, worked_date: e.target.value }))} style={inputStyle} disabled={!hasCompOffType} />
            </div>
            <div>
              <label style={labelStyle}>Days to Credit</label>
              <input type="number" step="0.5" min="0.5" value={form.earned_days} onChange={e => setForm(f => ({ ...f, earned_days: e.target.value }))} style={inputStyle} disabled={!hasCompOffType} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Note</label>
            <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Worked on project launch weekend" style={inputStyle} disabled={!hasCompOffType} />
          </div>
          <button
            onClick={() => {
              if (!form.user_id || !form.worked_date) { toast("Please fill in all required fields", "error"); return; }
              earnMut.mutate({ user_id: parseInt(form.user_id), worked_date: form.worked_date, earned_days: parseFloat(form.earned_days), note: form.note });
            }}
            disabled={earnMut.isPending || !hasCompOffType}
            style={{ ...primaryBtnStyle, opacity: (earnMut.isPending || !hasCompOffType) ? 0.6 : 1 }}>
            {earnMut.isPending ? "Recording…" : "Record Comp-off Day"}
          </button>
        </div>
      </div>

      <div>
        <h4 style={{ margin: "0 0 12px", fontSize: 14, color: "#1e293b" }}>Comp-off Earnings History</h4>
        {isLoading && <><SkeletonRow cols={3} /><SkeletonRow cols={3} /></>}
        {!isLoading && (earnings as any[]).length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>No comp-off earnings recorded yet.</div>
        )}
        <div style={{ display: "grid", gap: 8 }}>
          {(earnings as any[]).map((e: any) => (
            <div key={e.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 18px", display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #166534, #00c9a7)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {(e.user_name || "?")[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{e.user_name}</span>
                <span style={{ marginLeft: 10, fontSize: 13, color: "#64748b" }}>worked {e.worked_date}</span>
                {e.note && <span style={{ marginLeft: 8, fontSize: 12, color: "#94a3b8" }}>— {e.note}</span>}
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#166534" }}>+{e.earned_days} day{e.earned_days !== 1 ? "s" : ""}</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>by {e.creator_name}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── Leave Calendar Tab ────────────────────────────────────────────────────
function LeaveCalendarTab() {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());

  const { data: allRequests = [] } = useQuery({
    queryKey: ["leave-requests-all", year],
    queryFn: () => getLeaveRequests({ status: "approved" }),
  });

  const { data: types = [] } = useQuery({ queryKey: ["leave-types"], queryFn: getLeaveTypes });
  const typeColors: Record<number, string> = {};
  (types as any[]).forEach((t: any) => { typeColors[t.id] = t.color; });

  const calMap: Record<string, { name: string; color: string }[]> = {};
  (allRequests as any[]).forEach((req: any) => {
    if (req.status !== "approved") return;
    const start = new Date(req.start_date);
    const end = new Date(req.end_date);
    const cur = new Date(start);
    while (cur <= end) {
      if (cur.getMonth() + 1 === month && cur.getFullYear() === year) {
        const key = cur.toISOString().slice(0, 10);
        if (!calMap[key]) calMap[key] = [];
        calMap[key].push({ name: req.user_name, color: req.leave_type_color || "#1e3a8a" });
      }
      cur.setDate(cur.getDate() + 1);
    }
  });

  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  function prevMonth() { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); }
  function nextMonth() { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <button onClick={prevMonth} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 16 }}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 16, color: "#1e3a8a", minWidth: 120, textAlign: "center" }}>{monthNames[month - 1]} {year}</span>
        <button onClick={nextMonth} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 16 }}>›</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#94a3b8", padding: "4px 0" }}>{d}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} />;
          const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const entries = calMap[dateKey] || [];
          const isToday = day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();
          const isWeekend = (idx % 7 === 0 || idx % 7 === 6);
          return (
            <div key={idx} style={{ minHeight: 72, background: isWeekend ? "#f8fafc" : "white", border: isToday ? "2px solid #1e3a8a" : "1px solid #e2e8f0", borderRadius: 8, padding: "6px 8px", overflow: "hidden" }}>
              <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? "#1e3a8a" : isWeekend ? "#94a3b8" : "#475569", marginBottom: 4 }}>{day}</div>
              {entries.slice(0, 3).map((e, i) => (
                <div key={i} style={{ background: `${e.color}22`, borderLeft: `3px solid ${e.color}`, fontSize: 10, padding: "2px 4px", borderRadius: "0 3px 3px 0", marginBottom: 2, color: "#1e293b", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</div>
              ))}
              {entries.length > 3 && <div style={{ fontSize: 10, color: "#94a3b8" }}>+{entries.length - 3} more</div>}
            </div>
          );
        })}
      </div>
      {Object.keys(calMap).length === 0 && <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 13 }}>No approved leaves in {monthNames[month - 1]} {year}.</div>}
    </div>
  );
}


// ── Year-End Rollover Section ────────────────────────────────────────────────
function YearEndRolloverSection() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [fromYear, setFromYear] = useState(currentYear - 1);
  const [toYear, setToYear] = useState(currentYear);
  const [result, setResult] = useState<{ processed: number; from_year: number; to_year: number } | null>(null);

  const rolloverMut = useMutation({
    mutationFn: yearEndRollover,
    onSuccess: (data) => { setResult(data); qc.invalidateQueries({ queryKey: ["leave-team"] }); },
  });

  return (
    <div style={{ marginTop: 32, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: 20 }}>
      <LoadingOverlay active={rolloverMut.isPending} />
      <h3 style={{ margin: "0 0 10px", fontSize: 15, color: "#92400e" }}>Year-End Leave Rollover</h3>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#78350f" }}>Carry forward unused leave balances for all employees. Only leave types with "carry forward" enabled are processed.</p>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#78350f", display: "block", marginBottom: 4 }}>From Year</label>
          <select value={fromYear} onChange={e => setFromYear(parseInt(e.target.value))} style={{ border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", fontSize: 13, background: "white" }}>
            {[currentYear - 2, currentYear - 1, currentYear].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={{ fontSize: 18, color: "#92400e", paddingTop: 18 }}>→</div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#78350f", display: "block", marginBottom: 4 }}>To Year</label>
          <select value={toYear} onChange={e => setToYear(parseInt(e.target.value))} style={{ border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", fontSize: 13, background: "white" }}>
            {[currentYear - 1, currentYear, currentYear + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button
          onClick={() => confirm(`Roll over unused leave from ${fromYear} to ${toYear} for all employees? This cannot be undone.`, { title: "Year-End Rollover", confirmLabel: "Run Rollover" }).then(ok => { if (ok) rolloverMut.mutate({ from_year: fromYear, to_year: toYear }); })}
          disabled={rolloverMut.isPending || toYear <= fromYear}
          style={{ background: "#92400e", color: "white", border: "none", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13, marginTop: 18, opacity: rolloverMut.isPending || toYear <= fromYear ? 0.6 : 1 }}>
          {rolloverMut.isPending ? "Processing..." : "Run Rollover"}
        </button>
      </div>
      {rolloverMut.isError && <div style={{ marginTop: 12, color: "#dc2626", fontSize: 13 }}>Error: {(rolloverMut.error as any)?.response?.data?.detail || "Rollover failed"}</div>}
      {result && <div style={{ marginTop: 12, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#166534" }}>✅ Rollover complete — {result.processed} records updated from {result.from_year} → {result.to_year}.</div>}
    </div>
  );
}


// ── My Team Tab ─────────────────────────────────────────────────────────────

function MyTeamTab({ approveMut, rejectMut, cancelMut, rejectId, setRejectId, rejectReason, setRejectReason }: {
  approveMut: any; rejectMut: any; cancelMut: any;
  rejectId: number | null; setRejectId: (id: number | null) => void;
  rejectReason: string; setRejectReason: (r: string) => void;
}) {
  const { data: teamRequests = [], isLoading } = useQuery({
    queryKey: ["leave-requests-team"],
    queryFn: getTeamLeaveRequests,
  });
  const [statusFilter, setStatusFilter] = useState("pending");

  const filtered = (teamRequests as any[]).filter((r: any) => statusFilter === "" ? true : r.status === statusFilter);
  const pendingCount = (teamRequests as any[]).filter((r: any) => r.status === "pending").length;

  return (
    <div>
      {rejectId !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div className="modal-sheet" style={{ background: "white", borderRadius: 12, padding: 24 }}>
            <h3 style={{ margin: "0 0 14px", color: "#1e3a8a" }}>Reject Leave Request</h3>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection (optional)" style={{ width: "100%", height: 80, padding: 8, border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button onClick={() => setRejectId(null)} style={outlineBtnStyle}>Cancel</button>
              <button onClick={() => rejectMut.mutate({ id: rejectId!, reason: rejectReason })} style={{ ...outlineBtnStyle, background: "#dc2626", color: "white", borderColor: "#dc2626" }}>Reject</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Leave requests from your direct reports
          {pendingCount > 0 && <span style={{ marginLeft: 8, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{pendingCount} pending</span>}
        </div>
        <div className="filter-bar" style={{ display: "flex", gap: 6 }}>
          {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]].map(([s, label]) => (
            <button key={s || "all"} onClick={() => setStatusFilter(s)} style={{ padding: "5px 12px", borderRadius: 20, border: "1px solid #e2e8f0", background: statusFilter === s ? "#1e3a8a" : "white", color: statusFilter === s ? "white" : "#64748b", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>{label}</button>
          ))}
        </div>
      </div>

      {isLoading && <div style={{ display: "grid", gap: 10 }}><SkeletonRow cols={3} /><SkeletonRow cols={3} /></div>}
      {!isLoading && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
          <div style={{ fontWeight: 600, color: "#64748b", marginBottom: 4 }}>{(teamRequests as any[]).length === 0 ? "No direct reports assigned" : `No ${statusFilter} requests`}</div>
          <div style={{ fontSize: 13 }}>{(teamRequests as any[]).length === 0 ? "Assign yourself as manager to team members in Workspace → Team" : "Your team is all caught up"}</div>
        </div>
      )}

      <div style={{ display: "grid", gap: 10 }}>
        {filtered.map((req: any) => {
          const sc = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
          return (
            <div key={req.id} className="list-row" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg, #1e3a8a22, #0ea5e922)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "#1e3a8a" }}>
                    {(req.user_name || "?")[0].toUpperCase()}
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 15, color: "#1e293b" }}>{req.user_name}</span>
                  <span style={{ fontSize: 13, color: "#64748b" }}>· {req.leave_type}</span>
                  <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{req.status}</span>
                  {req.is_lwp && <span style={{ background: "#fce7f3", color: "#9d174d", border: "1px solid #fbcfe8", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>LWP</span>}
                </div>
                <div style={{ fontSize: 13, color: "#64748b", marginLeft: 42 }}>{formatDate(req.start_date)} → {formatDate(req.end_date)}<span style={{ marginLeft: 10, fontWeight: 600, color: "#1e293b" }}>{req.days} day{req.days !== 1 ? "s" : ""}{req.half_day ? " (half)" : ""}</span></div>
                {req.reason && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3, marginLeft: 42 }}>{req.reason}</div>}
              </div>
              <div className="request-actions" style={{ display: "flex", gap: 8 }}>
                {req.status === "pending" && (<>
                  <button onClick={() => approveMut.mutate(req.id)} style={{ background: "#dcfce7", color: "#166534", border: "1px solid #86efac", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Approve</button>
                  <button onClick={() => setRejectId(req.id)} style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Reject</button>
                </>)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


const primaryBtnStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, #1e3a8a, #00c9a7)",
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};

const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};

const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
